import React, { useState, useEffect, useRef } from "react";
import LazyImage from "../ui/LazyImage";
import { FolderPlus, Plus, Grid3x3, Folder, X, ToggleLeft, GripVertical, PenLine, Trash2, Check, Eye, EyeOff, ImageIcon } from "lucide-react";
import {
    getAllStrapOptionsAdmin,
    createStrapOption,
    updateStrapOption,
    deleteStrapOption,
    uploadChoiceImage,
    getStrapOptionCategories,
    createStrapOptionCategory,
    deleteStrapOptionCategory
} from "./fetchApi";
import ChoiceRowItem from "./options/ChoiceRowItem";
import ConditionalLogicSection from "./options/ConditionalLogicSection";

const apiURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const EMPTY_CHOICE = { value: '', label: '', tooltip: '', imageUrl: '', colorCode: '', tab: '', hasCustomInput: false, customInputPlaceholder: '' };
const EMPTY_RULE = { optionKey: '', operator: 'is', value: '' };
const EMPTY_FORM = {
    key: '', label: '', type: 'dropdown', categoryId: '', optionTooltip: '', descriptionTitle: '', description: '',
    contentGuide: '',
    useTabs: false, tabs: [],
    isRequired: false, order: 0, isActive: true, choices: [{ ...EMPTY_CHOICE }],
    textInstruction: '', textSubtitle: '', textPlaceholder: '', textLimit: 10,
    hasAddonToggle: false, addonLabel: '', addonPrice: 0, addonSubtitle: '',
    hasOnTone: false,
    conditions: { enabled: false, action: 'show', groupLogic: 'and', groups: [{ rules: [] }] }
};

const slugify = (str) => str.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const Options = ({ colors }) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    // category state
    const [categories, setCategories] = useState([]);
    const [activeTab, setActiveTab] = useState('ALL');
    const [catModalOpen, setCatModalOpen] = useState(false);
    const [catForm, setCatForm] = useState({ name: '', label: '' });
    const [catSaving, setCatSaving] = useState(false);

    // modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [uploadingIdx, setUploadingIdx] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [toast, setToast] = useState({ show: false, msg: '', ok: true });
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [draggedChoiceIdx, setDraggedChoiceIdx] = useState(null);

    const fileInputRef = useRef(null);
    const uploadChoiceIdxRef = useRef(null);

    const descFileInputRef = useRef(null);
    const [uploadingDescImg, setUploadingDescImg] = useState(false);

    const guideFileInputRef = useRef(null); // NEW REF
    const [uploadingGuideImg, setUploadingGuideImg] = useState(false);

    const showToast = (msg, ok = true) => {
        setToast({ show: true, msg, ok });
        setTimeout(() => setToast(t => ({ ...t, show: false })), 3200);
    };

    const fetchOptions = async (isMountedRef) => {
        setLoading(true);
        const [resOpts, resCats] = await Promise.all([
            getAllStrapOptionsAdmin(),
            getStrapOptionCategories()
        ]);
        if (!isMountedRef || isMountedRef.current) {
            setOptions(resOpts?.options || resOpts?.Options || []);
            const fetchedCats = resCats?.categories || [];
            setCategories(fetchedCats);
            // Default to 'ALL'
            setLoading(false);
        }
    };

    // ── Category CRUD ────────────────────────────────────────────────────────
    const handleSaveCategory = async () => {
        if (!catForm.label.trim()) { showToast('Category label is required', false); return; }
        setCatSaving(true);
        const payload = { name: catForm.name || slugify(catForm.label), label: catForm.label };
        try {
            const res = await createStrapOptionCategory(payload);
            if (res?.success) {
                showToast('Category created!');
                setCatModalOpen(false);
                setCatForm({ name: '', label: '' });
                fetchOptions();
            } else showToast(res?.error || 'Failed', false);
        } catch { showToast('Network error', false); }
        setCatSaving(false);
    };

    const handleDeleteCategory = async (id) => {
        if (!window.confirm('Delete this category?')) return;
        try {
            const res = await deleteStrapOptionCategory(id);
            if (res?.success) { showToast('Category deleted!'); fetchOptions(); }
            else showToast(res?.error || 'Failed', false);
        } catch { showToast('Network error', false); }
    };

    useEffect(() => {
        const isMountedRef = { current: true };
        fetchOptions(isMountedRef);
        return () => { isMountedRef.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Open modals ──────────────────────────────────────────────────────────
    const openCreate = () => {
        setEditTarget(null);
        setForm(EMPTY_FORM);
        setModalOpen(true);
    };

    const openEdit = (opt) => {
        setEditTarget(opt);
        setForm({
            key: opt.key,
            label: opt.label,
            type: opt.type,
            categoryId: (opt.categoryId && typeof opt.categoryId === 'object') ? opt.categoryId._id : (opt.categoryId || ''),
            optionTooltip: opt.optionTooltip || '',
            descriptionTitle: opt.descriptionTitle || '',
            description: opt.description || '',
            contentGuide: opt.contentGuide || '', // NEW
            useTabs: opt.useTabs || false,
            tabs: opt.tabs || [],
            isRequired: opt.isRequired,
            order: opt.order,
            isActive: opt.isActive,
            choices: opt.choices && opt.choices.length > 0
                ? opt.choices.map(c => ({ value: c.value, label: c.label, tooltip: c.tooltip || '', imageUrl: c.imageUrl || '', colorCode: c.colorCode || '', tab: c.tab || '', hasCustomInput: c.hasCustomInput || false, customInputPlaceholder: c.customInputPlaceholder || '' }))
                : [{ ...EMPTY_CHOICE }],
            textInstruction: opt.textInstruction || '',
            textSubtitle: opt.textSubtitle || '',
            textPlaceholder: opt.textPlaceholder || '',
            textLimit: opt.textLimit || 10,
            hasAddonToggle: opt.hasAddonToggle || false,
            addonLabel: opt.addonLabel || '',
            addonPrice: opt.addonPrice || 0,
            addonSubtitle: opt.addonSubtitle || '',
            hasOnTone: opt.hasOnTone || false,
            conditions: (() => {
                const c = opt.conditions;
                if (!c) return { enabled: false, action: 'show', groupLogic: 'and', groups: [{ rules: [] }] };
                if (c.groups) return { enabled: c.enabled || false, action: c.action || 'show', groupLogic: c.groupLogic || 'and', groups: c.groups };
                // Migrate old format { logic, rules } → one group
                return { enabled: c.enabled || false, action: 'show', groupLogic: 'and', groups: [{ rules: c.rules || [] }] };
            })()
        });
        setModalOpen(true);
    };

    const closeModal = () => { setModalOpen(false); setEditTarget(null); };

    // ── Form field helpers ───────────────────────────────────────────────────
    const setField = (name, value) => setForm(f => ({ ...f, [name]: value }));

    // ── Conditional Logic helpers ────────────────────────────────────────────
    const setCond = (key, val) => setForm(f => ({ ...f, conditions: { ...f.conditions, [key]: val } }));

    const addCondGroup = () => setForm(f => ({
        ...f,
        conditions: { ...f.conditions, groups: [...(f.conditions?.groups || []), { rules: [{ ...EMPTY_RULE }] }] }
    }));

    const removeCondGroup = (gIdx) => setForm(f => ({
        ...f,
        conditions: { ...f.conditions, groups: f.conditions.groups.filter((_, i) => i !== gIdx) }
    }));

    const addCondRule = (gIdx) => setForm(f => {
        const groups = (f.conditions?.groups || []).map((g, i) =>
            i !== gIdx ? g : { ...g, rules: [...(g.rules || []), { ...EMPTY_RULE }] }
        );
        return { ...f, conditions: { ...f.conditions, groups } };
    });

    const removeCondRule = (gIdx, rIdx) => setForm(f => {
        let groups = f.conditions.groups.map((g, i) => {
            if (i !== gIdx) return g;
            const rules = g.rules.filter((_, ri) => ri !== rIdx);
            return { ...g, rules };
        });
        // remove group if empty and more than 1 group
        if (groups[gIdx]?.rules.length === 0 && groups.length > 1) {
            groups = groups.filter((_, i) => i !== gIdx);
        }
        return { ...f, conditions: { ...f.conditions, groups } };
    });

    const setCondRule = (gIdx, rIdx, key, val) => setForm(f => {
        const groups = f.conditions.groups.map((g, gi) =>
            gi !== gIdx ? g : {
                ...g,
                rules: g.rules.map((r, ri) =>
                    ri !== rIdx ? r : { ...r, [key]: val, ...(key === 'optionKey' ? { value: '' } : {}) }
                )
            }
        );
        return { ...f, conditions: { ...f.conditions, groups } };
    });

    const setChoice = (idx, name, value) => {
        setForm(f => {
            const choices = [...f.choices];
            choices[idx] = { ...choices[idx], [name]: value };
            // auto-populate value slug from label ALWAYS since input is hidden
            if (name === 'label') {
                choices[idx].value = slugify(value);
            }
            return { ...f, choices };
        });
    };

    const addChoice = (tabName = '') => setForm(f => ({ ...f, choices: [...f.choices, { ...EMPTY_CHOICE, tab: tabName }] }));

    const removeChoice = (idx) => setForm(f => ({
        ...f, choices: f.choices.filter((_, i) => i !== idx)
    }));

    const handleChoiceDrop = (targetIdx) => {
        if (draggedChoiceIdx === null || draggedChoiceIdx === targetIdx) return;
        setForm(f => {
            const choices = [...f.choices];
            const [moved] = choices.splice(draggedChoiceIdx, 1);
            choices.splice(targetIdx, 0, moved);
            return { ...f, choices };
        });
        setDraggedChoiceIdx(null);
    };

    const handleRemoveTab = (tabName) => {
        if (window.confirm(`Remove tab "${tabName}" and all its choices?`)) {
            setForm(f => ({
                ...f,
                tabs: f.tabs.filter(t => t !== tabName),
                choices: f.choices.filter(c => c.tab !== tabName)
            }));
        }
    };

    // ── Image upload for a choice ────────────────────────────────────────────
    const handleChoiceImageClick = (idx) => {
        uploadChoiceIdxRef.current = idx;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    const handleChoiceImageFile = async (e) => {
        const inputEl = e.target; // save ref before async
        const file = inputEl.files?.[0];
        if (!file) return;
        const idx = uploadChoiceIdxRef.current;
        setUploadingIdx(idx);
        const fd = new FormData();
        fd.append('image', file);
        const res = await uploadChoiceImage(fd);
        if (res?.success && res?.imageUrl) {
            setChoice(idx, 'imageUrl', res.imageUrl);
            showToast('Image uploaded!');
        } else {
            showToast(res?.error || 'Upload failed', false);
        }
        setUploadingIdx(null);
        if (inputEl) inputEl.value = '';
    };

    // ── Image upload for Description ─────────────────────────────────────────
    const handleDescImageClick = () => {
        if (descFileInputRef.current) descFileInputRef.current.click();
    };

    const handleDescImageFile = async (e) => {
        const inputEl = e.target;
        const file = inputEl.files?.[0];
        if (!file) return;
        setUploadingDescImg(true);
        const fd = new FormData();
        fd.append('image', file);
        const res = await uploadChoiceImage(fd);
        if (res?.success && res?.imageUrl) {
            const imgUrl = res.imageUrl.startsWith('http') ? res.imageUrl : `${apiURL}${res.imageUrl}`;
            const imgTag = `\n<img src="${imgUrl}" style="width:100%; border-radius:8px; margin:15px 0;" alt="description image"/>\n`;
            setForm(f => ({ ...f, description: (f.description || '') + imgTag }));
            showToast('Image inserted into description!');
        } else {
            showToast(res?.error || 'Upload failed', false);
        }
        setUploadingDescImg(false);
        if (inputEl) inputEl.value = '';
    };

    // ── Image upload for Content Guide ───────────────────────────────────────
    const handleGuideImageClick = () => {
        if (guideFileInputRef.current) guideFileInputRef.current.click();
    };

    const handleGuideImageFile = async (e) => {
        const inputEl = e.target;
        const file = inputEl.files?.[0];
        if (!file) return;
        setUploadingGuideImg(true);
        const fd = new FormData();
        fd.append('image', file);
        const res = await uploadChoiceImage(fd);
        if (res?.success && res?.imageUrl) {
            const imgUrl = res.imageUrl.startsWith('http') ? res.imageUrl : `${apiURL}${res.imageUrl}`;
            const imgTag = `\n<img src="${imgUrl}" style="width:100%; border-radius:8px; margin:15px 0;" alt="guide image"/>\n`;
            setForm(f => ({ ...f, contentGuide: (f.contentGuide || '') + imgTag }));
            showToast('Image inserted into guide!');
        } else {
            showToast(res?.error || 'Upload failed', false);
        }
        setUploadingGuideImg(false);
        if (inputEl) inputEl.value = '';
    };

    const renderChoiceRow = (choice, idx) => (
        <ChoiceRowItem
            key={idx}
            choice={choice}
            idx={idx}
            type={form.type}
            colors={colors}
            uploadingIdx={uploadingIdx}
            draggedChoiceIdx={draggedChoiceIdx}
            inputStyle={inputStyle}
            onSetChoice={setChoice}
            onImageClick={handleChoiceImageClick}
            onDragStart={setDraggedChoiceIdx}
            onDragEnd={() => setDraggedChoiceIdx(null)}
            onDrop={handleChoiceDrop}
            onRemove={removeChoice}
            showRemove={form.choices.length > 1}
        />
    );

    // ── Save (create/update) ─────────────────────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.key.trim() || !form.label.trim()) {
            showToast('Key and Label are required', false); return;
        }

        // Filter valid choices depending on tab mode
        const validChoices = form.useTabs 
            ? form.choices.filter(c => form.tabs.includes(c.tab))
            : form.choices;

        if (form.type !== 'custom-text') {
            if (validChoices.length === 0) {
                showToast('Please add at least one choice', false); return;
            }

            if (validChoices.some(c => !c.value.trim() || !c.label.trim())) {
                showToast('All choices need a value and label', false); return;
            }
        }

        setSaving(true);
        const payload = {
            key: form.key.trim(),
            label: form.label.trim(),
            type: form.type,
            categoryId: form.categoryId || null,
            optionTooltip: form.optionTooltip || '',
            descriptionTitle: form.descriptionTitle || '',
            description: form.description || '',
            contentGuide: form.contentGuide || '', // NEW
            useTabs: form.useTabs || false,
            tabs: form.tabs || [],
            isRequired: form.isRequired,
            order: Number(form.order) || 0,
            isActive: form.isActive,
            choices: form.type === 'custom-text' ? [] : validChoices.map(c => ({
                value: c.value.trim(),
                label: c.label.trim(),
                tooltip: c.tooltip || '',
                imageUrl: c.imageUrl || '',
                colorCode: c.colorCode || '',
                tab: c.tab || '',
                hasCustomInput: c.hasCustomInput || false,
                customInputPlaceholder: c.customInputPlaceholder || ''
            })),
            textInstruction: form.textInstruction || '',
            textSubtitle: form.textSubtitle || '',
            textPlaceholder: form.textPlaceholder || '',
            textLimit: Number(form.textLimit) || 10,
            hasAddonToggle: form.hasAddonToggle || false,
            addonLabel: form.addonLabel || '',
            addonPrice: Number(form.addonPrice) || 0,
            addonSubtitle: form.addonSubtitle || '',
            hasOnTone: form.hasOnTone || false,
            conditions: form.conditions || { enabled: false, action: 'show', groupLogic: 'and', groups: [{ rules: [] }] }
        };
        const res = editTarget
            ? await updateStrapOption(editTarget._id, payload)
            : await createStrapOption(payload);
        setSaving(false);
        if (res?.success) {
            showToast(editTarget ? 'Option updated!' : 'Option created!');
            closeModal();
            fetchOptions();
        } else {
            showToast(res?.error || 'Save failed', false);
        }
    };

    // ── Delete ───────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        const res = await deleteStrapOption(id);
        setDeleteConfirm(null);
        if (res?.success) { showToast('Option deleted!'); fetchOptions(); }
        else showToast(res?.error || 'Delete failed', false);
    };

    // ── Toggle active ────────────────────────────────────────────────────────
    const handleToggleActive = async (opt) => {
        const res = await updateStrapOption(opt._id, { isActive: !opt.isActive });
        if (res?.success) fetchOptions();
    };

    // ── Drag & Drop Reordering ───────────────────────────────────────────────
    const handleDrop = async (targetOpt) => {
        if (!draggedId || draggedId === targetOpt._id) return;

        const fullSorted = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
        const draggedAbsIdx = fullSorted.findIndex(o => o._id === draggedId);
        const targetAbsIdx = fullSorted.findIndex(o => o._id === targetOpt._id);
        if (draggedAbsIdx === -1 || targetAbsIdx === -1) return;

        // Reposition dragged item to target's absolute position
        const newFull = [...fullSorted];
        const [movedItem] = newFull.splice(draggedAbsIdx, 1);
        newFull.splice(targetAbsIdx, 0, movedItem);

        // Update local state optimistically for instant feedback
        const updatedList = newFull.map((item, idx) => ({ ...item, order: idx }));
        setOptions(updatedList);
        setDraggedId(null);
        setDragOverId(null);

        // Sync with backend in background
        try {
            await Promise.all(updatedList.map((item, idx) => updateStrapOption(item._id, { order: idx })));
        } catch (err) {
            console.error('Failed to sync order:', err);
            fetchOptions(); // Fallback if error
        }
    };

    // ── Styles ───────────────────────────────────────────────────────────────
    const cardStyle = {
        backgroundColor: colors?.bgCard || '#fff',
        border: `1px solid ${colors?.borderColor || '#E9E4DB'}`,
        borderRadius: '16px',
    };

    const inputStyle = {
        border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}`,
        borderRadius: '10px',
        padding: '9px 13px',
        fontSize: '0.9rem',
        backgroundColor: colors?.bgApp || '#FCFBF7',
        color: colors?.textMain || '#1A110B',
        outline: 'none',
        width: '100%',
    };

    const labelStyle = {
        fontSize: '0.78rem', fontWeight: '700', letterSpacing: '0.8px',
        textTransform: 'uppercase', color: colors?.textMuted || '#8B7D6B',
        marginBottom: '6px', display: 'block'
    };

    const typeColors = { dropdown: '#6366f1', 'image-select': '#f59e0b', 'image-choice': '#10b981' };

    return (
        <div>
            {/* Remove number input spinners globally */}
            <style>{`
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
                input[type=number] { -moz-appearance: textfield; }
            `}</style>
            {/* hidden file inputs */}
            <input type="file" accept="image/*" ref={fileInputRef} className="d-none" onChange={handleChoiceImageFile} />
            <input type="file" accept="image/*" ref={descFileInputRef} className="d-none" onChange={handleDescImageFile} />
            <input type="file" accept="image/*" ref={guideFileInputRef} className="d-none" onChange={handleGuideImageFile} />

            {/* TOAST */}
            {toast.show && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: toast.ok ? '#1A110B' : '#ef4444', color: '#fff',
                    padding: '10px 22px', borderRadius: '50px', fontSize: '13px', fontWeight: '600',
                    zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', whiteSpace: 'nowrap'
                }}>
                    <i className={`bi ${toast.ok ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} me-2`}></i>
                    {toast.msg}
                </div>
            )}

            {/* HEADER */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h4 className="fw-bold m-0" style={{ color: colors?.textMain }}>Strap Options</h4>
                    <p className="text-muted mb-0 small">Manage configurable options shown to customers in the sidebar</p>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn fw-bold shadow-sm d-flex align-items-center gap-2"
                        style={{ backgroundColor: colors?.bgCard || '#fff', color: colors?.textMuted || '#8B7D6B', borderRadius: '12px', padding: '9px 16px', border: `1px solid ${colors?.borderColor || '#E9E4DB'}` }}
                        onClick={() => { setCatForm({ name: '', label: '' }); setCatModalOpen(true); }}>
                        <FolderPlus size={16} /> New Category
                    </button>
                    <button className="btn fw-bold shadow-sm d-flex align-items-center gap-2"
                        style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '12px', padding: '9px 16px' }}
                        onClick={openCreate}>
                        <Plus size={16} /> New Option
                    </button>
                </div>
            </div>

            {/* CATEGORY CHIPS / TABS */}
            <div className="d-flex flex-wrap gap-2 mb-4">
                <button type="button" 
                    onClick={() => setActiveTab('ALL')}
                    className="d-flex align-items-center gap-1 px-3 py-2"
                    style={{ 
                        border: activeTab === 'ALL' ? `1px solid #C5A075` : `1px solid ${colors?.borderColor || '#E9E4DB'}`, 
                        borderRadius: '20px', 
                        backgroundColor: activeTab === 'ALL' ? '#FEFCF8' : (colors?.bgCard || '#fff'), 
                        fontSize: '0.85rem', fontWeight: '700', 
                        color: activeTab === 'ALL' ? '#C5A075' : (colors?.textMain || '#1A110B'),
                        cursor: 'pointer', transition: 'all 0.2s'
                    }}>
                    <Grid3x3 size={12} className="me-1" />
                    All Options
                </button>
                {categories.map(cat => (
                    <div key={cat._id} className="d-flex align-items-center gap-1 px-3 py-2"
                        onClick={() => setActiveTab(cat._id)}
                        style={{ 
                            border: activeTab === cat._id ? `1px solid #C5A075` : `1px solid ${colors?.borderColor || '#E9E4DB'}`, 
                            borderRadius: '20px', 
                            backgroundColor: activeTab === cat._id ? '#FEFCF8' : (colors?.bgCard || '#fff'), 
                            fontSize: '0.85rem', fontWeight: '700', 
                            color: activeTab === cat._id ? '#C5A075' : (colors?.textMain || '#1A110B'),
                            cursor: 'pointer', transition: 'all 0.2s'
                        }}>
                        <Folder size={12} className="me-1" />
                        {cat.label}
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat._id); }}
                            style={{ background: 'none', border: 'none', padding: '0 0 0 6px', cursor: 'pointer', color: '#ef4444', lineHeight: 1 }}>
                            <X size={12} />
                        </button>
                    </div>
                ))}
            </div>

            {/* LIST */}
            {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border" style={{ color: '#C5A075' }}></div>
                </div>
            ) : options.length === 0 ? (
                <div className="text-center py-5" style={cardStyle}>
                    <ToggleLeft size={48} color="#E9E4DB" />
                    <p className="text-muted mt-3 mb-0">No options yet. Create your first one.</p>
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    {[...options]
                        .filter(opt => activeTab === 'ALL' || opt.categoryId === activeTab || (typeof opt.categoryId === 'object' && opt.categoryId?._id === activeTab))
                        .sort((a, b) => a.order - b.order)
                        .map(opt => (
                        <div 
                            key={opt._id} 
                            className="p-4" 
                            draggable
                            onDragStart={() => setDraggedId(opt._id)}
                            onDragOver={(e) => {
                                e.preventDefault();
                                if (dragOverId !== opt._id) setDragOverId(opt._id);
                            }}
                            onDragLeave={() => setDragOverId(null)}
                            onDragEnd={() => {
                                setDraggedId(null);
                                setDragOverId(null);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragOverId(null);
                                handleDrop(opt);
                            }}
                            style={{
                                ...cardStyle,
                                opacity: opt.isActive ? (draggedId === opt._id ? 0.4 : 1) : 0.6,
                                border: dragOverId === opt._id ? '2px dashed #C5A075' : cardStyle.border,
                                transform: dragOverId === opt._id ? 'scale(1.01)' : 'none',
                                transition: 'all 0.2s',
                                cursor: 'grab'
                            }}
                        >
                            <div className="d-flex align-items-start justify-content-between gap-3">
                                <div className="d-flex align-items-start gap-3 flex-grow-1 min-width-0">
                                    {/* Drag Grip Handle */}
                                    <div style={{ color: '#D0C8BF', cursor: 'grab', display: 'flex', alignItems: 'center', height: '44px' }} title="Drag to reorder">
                                        <GripVertical size={20} />
                                    </div>
                                    {/* Icon box */}
                                    <div style={{
                                        width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                                        backgroundColor: opt.type === 'image-select' ? '#FEF3C7' : opt.type === 'image-choice' ? '#D1FAE5' : '#EEF2FF',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <ImageIcon size={18} style={{ color: typeColors[opt.type] || '#6366f1' }} />
                                    </div>
                                    <div className="min-width-0">
                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                            <span className="fw-bold" style={{ color: colors?.textMain, fontSize: '1rem' }}>{opt.label}</span>
                                            <span style={{
                                                fontSize: '0.7rem', fontWeight: '700', padding: '2px 8px',
                                                borderRadius: '20px', textTransform: 'uppercase',
                                                backgroundColor: typeColors[opt.type] + '22',
                                                color: typeColors[opt.type] || '#6366f1'
                                            }}>{opt.type}</span>
                                            {opt.categoryId && categories.find(c => c._id === opt.categoryId) && (
                                                <span style={{ fontSize: '0.7rem', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', backgroundColor: '#FEF3C7', color: '#92400E' }}>
                                                    <Folder size={11} className="me-1" />
                                                    {categories.find(c => c._id === opt.categoryId)?.label}
                                                </span>
                                            )}
                                            {opt.isRequired && <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', backgroundColor: '#fee2e2', color: '#ef4444' }}>Required</span>}
                                        </div>
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {(opt.choices || []).map((c, ci) => (
                                                <div key={c._id || `${c.value}-${ci}`} className="d-flex align-items-center gap-1"
                                                    style={{ border: '1px solid #E9E4DB', borderRadius: '8px', padding: '3px 10px', fontSize: '0.78rem', backgroundColor: '#FCFBF7' }}>
                                                    {c.imageUrl && <LazyImage src={c.imageUrl.startsWith('http') ? c.imageUrl : `${apiURL}${c.imageUrl}`} alt={c.label} style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '3px', backgroundColor: '#F7F3EE', flexShrink: 0 }} />}
                                                    <span style={{ color: '#1A110B', fontWeight: '600' }}>{c.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {/* Actions */}
                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                    <span className="text-muted small me-1" style={{ fontSize: '0.75rem', fontWeight: '600', minWidth: '20px', textAlign: 'center' }}>#{opt.order}</span>
                                    <button
                                        className="btn btn-sm"
                                        title={opt.isActive ? 'Deactivate' : 'Activate'}
                                        onClick={() => handleToggleActive(opt)}
                                        style={{
                                            borderRadius: '8px', border: '1px solid #E9E4DB',
                                            backgroundColor: opt.isActive ? '#dcfce7' : '#f3f4f6',
                                            color: opt.isActive ? '#16a34a' : '#6b7280',
                                            padding: '6px 10px'
                                        }}>
                                        {opt.isActive ? <Eye size={15} /> : <EyeOff size={15} />}
                                    </button>
                                    <button className="btn btn-sm"
                                        onClick={() => openEdit(opt)}
                                        style={{ borderRadius: '8px', border: '1px solid #E9E4DB', padding: '6px 10px', backgroundColor: '#EEF2FF', color: '#6366f1' }}>
                                        <PenLine size={14} />
                                    </button>
                                    <button className="btn btn-sm"
                                        onClick={() => setDeleteConfirm(opt._id)}
                                        style={{ borderRadius: '8px', border: '1px solid #FEE2E2', padding: '6px 10px', backgroundColor: '#FEF2F2', color: '#ef4444' }}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── DELETE CONFIRM MODAL ──────────────────────────────────────────── */}
            {deleteConfirm && (
                <div className="position-fixed w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ top: 0, left: 0, zIndex: 8000, backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="p-4 shadow-lg" style={{ ...cardStyle, maxWidth: '360px', width: '90%', borderRadius: '20px' }}>
                        <div className="text-center mb-3">
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#FEF2F2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
                                <Trash2 size={21} color="#ef4444" />
                            </div>
                            <h6 className="fw-bold mb-1" style={{ color: colors?.textMain }}>Delete Option?</h6>
                            <p className="text-muted mb-0 small">This cannot be undone. Existing orders with this option are not affected.</p>
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn flex-grow-1 fw-bold" onClick={() => setDeleteConfirm(null)}
                                style={{ border: '1.5px solid #E9E4DB', borderRadius: '10px', color: colors?.textMuted }}>Cancel</button>
                            <button className="btn flex-grow-1 fw-bold" onClick={() => handleDelete(deleteConfirm)}
                                style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: '10px' }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CREATE / EDIT MODAL ───────────────────────────────────────────── */}
            {modalOpen && (
                <div className="position-fixed w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ top: 0, left: 0, zIndex: 8000, backgroundColor: 'rgba(0,0,0,0.45)' }}>
                    <div className="shadow-lg d-flex flex-column"
                        style={{
                            ...cardStyle, borderRadius: '20px',
                            width: '96%', maxWidth: '700px',
                            maxHeight: '92vh', overflow: 'hidden'
                        }}>
                        {/* Modal Header */}
                        <div className="d-flex align-items-center justify-content-between px-4 py-3"
                            style={{ borderBottom: `1px solid ${colors?.borderColor || '#E9E4DB'}` }}>
                            <h6 className="fw-bold m-0" style={{ color: colors?.textMain }}>
                                {editTarget ? 'Edit Option' : 'New Option'}
                            </h6>
                            <button className="btn btn-sm" onClick={closeModal}
                                style={{ border: 'none', backgroundColor: 'transparent', color: colors?.textMuted }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <form onSubmit={handleSave} className="overflow-auto flex-grow-1 px-4 py-4">
                            {/* Label only (key auto-generated) */}
                            <div className="mb-3">
                                <label style={labelStyle}>Label <span style={{ color: '#ef4444' }}>*</span></label>
                                <input style={inputStyle} value={form.label}
                                    onChange={e => {
                                        setField('label', e.target.value);
                                        if (!editTarget) setField('key', slugify(e.target.value));
                                    }}
                                    placeholder="e.g. Padding Style" required />
                                {/* Hidden key */}
                                <input type="hidden" value={form.key} readOnly />
                            </div>

                            <div className="mb-3">
                                <label style={labelStyle}>Option Tooltip (Optional)</label>
                                <input style={inputStyle} value={form.optionTooltip}
                                    onChange={e => setField('optionTooltip', e.target.value)}
                                    placeholder="Hover text for the main sidebar tab" />
                            </div>

                            <div className="mb-3">
                                <label style={labelStyle}>Description Title (Optional)</label>
                                <input style={inputStyle} value={form.descriptionTitle}
                                    onChange={e => setField('descriptionTitle', e.target.value)}
                                    placeholder="e.g. What is Padded?" />
                            </div>

                            <div className="mb-3">
                                <label style={labelStyle}>
                                    Description Content (Popup)
                                    <button type="button" onClick={handleDescImageClick} disabled={uploadingDescImg}
                                        style={{ float: 'right', background: 'none', border: 'none', color: '#6366f1', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>
                                        {uploadingDescImg ? 'Uploading...' : '+ Insert Image'}
                                    </button>
                                </label>
                                <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.description}
                                    onChange={e => setField('description', e.target.value)}
                                    placeholder="Explanation to show in the popup (HTML supported)" />
                            </div>

                            <div className="mb-3">
                                <label style={labelStyle}>
                                    Bottom Content Guide (Sticky below option)
                                    <button type="button" onClick={handleGuideImageClick} disabled={uploadingGuideImg}
                                        style={{ float: 'right', background: 'none', border: 'none', color: '#6366f1', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}>
                                        {uploadingGuideImg ? 'Uploading...' : '+ Insert Image'}
                                    </button>
                                </label>
                                <textarea style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} value={form.contentGuide}
                                    onChange={e => setField('contentGuide', e.target.value)}
                                    placeholder="Long guide shown at the bottom (HTML supported)" />
                            </div>

                            {/* Type + Category + Active/Required */}
                            <div className="d-flex align-items-end gap-3 mb-3">
                                <div style={{ flex: '1 1 0' }}>
                                    <label style={labelStyle}>Type</label>
                                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.type}
                                        onChange={e => setField('type', e.target.value)}>
                                        <option value="dropdown">Dropdown (List)</option>
                                        <option value="image-select">Image Select (Small tiles)</option>
                                        <option value="image-choice">Image Choice (Large cards)</option>
                                        <option value="color-picker">Color Picker (Color circles)</option>
                                        <option value="custom-text">Custom Text / Personalization</option>
                                    </select>
                                </div>
                                <div style={{ flex: '1 1 0' }}>
                                    <label style={labelStyle}>Category</label>
                                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.categoryId}
                                        onChange={e => setField('categoryId', e.target.value)}>
                                        <option value="">— None —</option>
                                        {categories.map(cat => (
                                            <option key={cat._id} value={cat._id}>{cat.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="d-flex gap-4 align-items-center" style={{ flex: '1 1 0', paddingBottom: '8px' }}>
                                    <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setField('isRequired', !form.isRequired)}>
                                        <div style={{
                                            width: '36px', height: '20px', borderRadius: '20px',
                                            backgroundColor: form.isRequired ? '#C5A075' : '#D1D5DB',
                                            position: 'relative', transition: 'background-color 0.2s',
                                            display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0
                                        }}>
                                            <div style={{
                                                width: '16px', height: '16px', borderRadius: '50%',
                                                backgroundColor: '#fff',
                                                transform: form.isRequired ? 'translateX(16px)' : 'translateX(0)',
                                                transition: 'transform 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                            }} />
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors?.textMain, userSelect: 'none' }}>Required</span>
                                    </div>

                                    <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setField('useTabs', !form.useTabs)}>
                                        <div style={{
                                            width: '36px', height: '20px', borderRadius: '20px',
                                            backgroundColor: form.useTabs ? '#C5A075' : '#D1D5DB',
                                            position: 'relative', transition: 'background-color 0.2s',
                                            display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0
                                        }}>
                                            <div style={{
                                                width: '16px', height: '16px', borderRadius: '50%',
                                                backgroundColor: '#fff',
                                                transform: form.useTabs ? 'translateX(16px)' : 'translateX(0)',
                                                transition: 'transform 0.2s',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                            }} />
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors?.textMain, userSelect: 'none' }}>Use Tabs</span>
                                    </div>
                                </div>
                            </div>

                            {/* CHOICES / TEXT INPUTS CONFIG */}
                            {form.type === 'custom-text' ? (
                                <div style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}`, paddingTop: '16px', marginBottom: '8px' }}>
                                    <label style={{ ...labelStyle, marginBottom: '12px', fontSize: '0.9rem', color: colors?.textMain || '#fff' }}>Text Input Configuration</label>
                                    
                                    <div className="mb-3">
                                        <label style={labelStyle}>Main Instruction / Title</label>
                                        <input style={inputStyle} value={form.textInstruction || ''}
                                            onChange={e => setField('textInstruction', e.target.value)}
                                            placeholder="e.g. The text you want to stamp" />
                                    </div>
                                    <div className="mb-3">
                                        <label style={labelStyle}>Subtitle / Details</label>
                                        <input style={inputStyle} value={form.textSubtitle || ''}
                                            onChange={e => setField('textSubtitle', e.target.value)}
                                            placeholder="e.g. We can't stamp other brand names on the strap." />
                                    </div>
                                    <div className="d-flex gap-3 mb-4">
                                        <div style={{ flex: '2 1 0' }}>
                                            <label style={labelStyle}>Input Placeholder</label>
                                            <input style={inputStyle} value={form.textPlaceholder || ''}
                                                onChange={e => setField('textPlaceholder', e.target.value)}
                                                placeholder="e.g. Limit 10 character" />
                                        </div>
                                        <div style={{ flex: '1 1 0' }}>
                                            <label style={labelStyle}>Character Limit</label>
                                            <input type="number" style={inputStyle} value={form.textLimit || 10}
                                                onChange={e => setField('textLimit', e.target.value)}
                                                placeholder="10" />
                                        </div>
                                    </div>

                                    {/* Addon Toggle Config */}
                                    <div className="p-3 mb-3" style={{ border: `1px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.01)' }}>
                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: colors?.textMain || '#fff' }}>Enable Optional Addon Toggle Switch</span>
                                            <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setField('hasAddonToggle', !form.hasAddonToggle)}>
                                                <div style={{
                                                    width: '36px', height: '20px', borderRadius: '20px',
                                                    backgroundColor: form.hasAddonToggle ? '#C5A075' : '#D1D5DB',
                                                    position: 'relative', transition: 'background-color 0.2s',
                                                    display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0
                                                }}>
                                                    <div style={{
                                                        width: '16px', height: '16px', borderRadius: '50%',
                                                        backgroundColor: '#fff',
                                                        transform: form.hasAddonToggle ? 'translateX(16px)' : 'translateX(0)',
                                                        transition: 'transform 0.2s',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                </div>
                                                <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors?.textMain || '#fff', userSelect: 'none' }}>{form.hasAddonToggle ? 'ON' : 'OFF'}</span>
                                            </div>
                                        </div>

                                        {form.hasAddonToggle && (
                                            <div className="d-flex flex-column gap-3 pt-2" style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}` }}>
                                                <div className="d-flex gap-3">
                                                    <div style={{ flex: '2 1 0' }}>
                                                        <label style={labelStyle}>Toggle Label</label>
                                                        <input style={inputStyle} value={form.addonLabel || ''}
                                                            onChange={e => setField('addonLabel', e.target.value)}
                                                            placeholder="e.g. No engrave logo" />
                                                    </div>
                                                    <div style={{ flex: '1 1 0' }}>
                                                        <label style={labelStyle}>Extra Fee ($)</label>
                                                        <input type="number" style={inputStyle} value={form.addonPrice || 0}
                                                            onChange={e => setField('addonPrice', e.target.value)}
                                                            placeholder="25" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label style={labelStyle}>Toggle Subtitle / Explanation</label>
                                                    <input style={inputStyle} value={form.addonSubtitle || ''}
                                                        onChange={e => setField('addonSubtitle', e.target.value)}
                                                        placeholder="e.g. Without HANDDN logo, there's an extra $25 fee" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : form.type === 'color-picker' ? (
                                <div style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}`, paddingTop: '16px', marginBottom: '8px' }}>
                                    <div className="p-3 mb-4" style={{ border: `1px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', backgroundColor: 'rgba(255, 255, 255, 0.01)' }}>
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                                                <div>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: colors?.textMain || '#fff', display: 'block' }}>Enable "On tone" Choice</span>
                                                    <span style={{ fontSize: '0.72rem', color: colors?.textMuted || '#8B7D6B' }}>Automatically adds an "On tone" option at the beginning</span>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setField('hasOnTone', !form.hasOnTone)}>
                                                <div style={{
                                                    width: '36px', height: '20px', borderRadius: '20px',
                                                    backgroundColor: form.hasOnTone ? '#C5A075' : '#D1D5DB',
                                                    position: 'relative', transition: 'background-color 0.2s',
                                                    display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0
                                                }}>
                                                    <div style={{
                                                        width: '16px', height: '16px', borderRadius: '50%',
                                                        backgroundColor: '#fff',
                                                        transform: form.hasOnTone ? 'translateX(16px)' : 'translateX(0)',
                                                        transition: 'transform 0.2s',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                    }} />
                                                </div>
                                                <span style={{ fontSize: '0.82rem', fontWeight: '600', color: colors?.textMain || '#fff', userSelect: 'none' }}>{form.hasOnTone ? 'ON' : 'OFF'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {!form.useTabs ? (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <label style={{ ...labelStyle, marginBottom: 0 }}>Color Choices</label>
                                                <button type="button" className="btn btn-sm fw-bold"
                                                    style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '8px', padding: '4px 12px', fontSize: '0.8rem' }}
                                                    onClick={() => addChoice('')}>
                                                    <Plus size={13} className="me-1" />Add
                                                </button>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                {form.choices.map((choice, idx) => renderChoiceRow(choice, idx))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <label style={{ ...labelStyle, marginBottom: 0 }}>Color Tabs & Choices</label>
                                                <div className="d-flex gap-2">
                                                    <input type="text" id="newTabInputColor" 
                                                        style={{ ...inputStyle, padding: '4px 10px', fontSize: '0.8rem', width: '150px', height: '28px', minHeight: '28px' }} 
                                                        placeholder="New tab name..." 
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                const btn = document.getElementById('addTabBtnColor');
                                                                if (btn) btn.click();
                                                            }
                                                        }}
                                                    />
                                                    <button type="button" id="addTabBtnColor" className="btn btn-sm fw-bold d-flex align-items-center"
                                                        style={{ backgroundColor: '#1E2335', color: '#fff', borderRadius: '8px', padding: '0 12px', fontSize: '0.8rem', height: '28px' }}
                                                        onClick={() => {
                                                            const el = document.getElementById('newTabInputColor');
                                                            if (el && el.value.trim() !== '') {
                                                                const val = el.value.trim();
                                                                setForm(f => {
                                                                    if (f.tabs.includes(val)) return f;
                                                                    return { ...f, tabs: [...f.tabs, val] };
                                                                });
                                                                el.value = '';
                                                            }
                                                        }}>
                                                        <Plus size={13} className="me-1" />Add Tab
                                                    </button>
                                                </div>
                                            </div>
                                            {form.tabs.map(tab => (
                                                <div key={tab} className="mb-4" style={{ border: `1px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', padding: '15px', backgroundColor: 'transparent' }}>
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <h6 style={{ fontWeight: '700', color: colors?.textMain || '#fff', margin: 0 }}>Tab: {tab}</h6>
                                                        <div>
                                                            <button type="button" className="btn btn-sm fw-bold me-2"
                                                                style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '0.75rem' }}
                                                                onClick={() => addChoice(tab)}>
                                                                <Plus size={13} className="me-1" />Add Color
                                                            </button>
                                                            <button type="button" className="btn btn-sm fw-bold"
                                                                style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '0.75rem' }}
                                                                onClick={() => handleRemoveTab(tab)}>
                                                                Remove Tab
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        {form.choices.map((c, i) => ({ ...c, originalIdx: i })).filter(c => c.tab === tab).map((choice) => renderChoiceRow(choice, choice.originalIdx))}
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}`, paddingTop: '16px', marginBottom: '8px' }}>
                                    {!form.useTabs ? (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <label style={{ ...labelStyle, marginBottom: 0 }}>Choices</label>
                                                <button type="button" className="btn btn-sm fw-bold"
                                                    style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '8px', padding: '4px 12px', fontSize: '0.8rem' }}
                                                    onClick={() => addChoice('')}>
                                                    <Plus size={13} className="me-1" />Add
                                                </button>
                                            </div>
                                            <div className="d-flex flex-column gap-2">
                                                {form.choices.map((choice, idx) => renderChoiceRow(choice, idx))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <label style={{ ...labelStyle, marginBottom: 0 }}>Tabs & Choices</label>
                                                <div className="d-flex gap-2">
                                                    <input type="text" id="newTabInput" 
                                                        style={{ ...inputStyle, padding: '4px 10px', fontSize: '0.8rem', width: '150px', height: '28px', minHeight: '28px' }} 
                                                        placeholder="New tab name..." 
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                const btn = document.getElementById('addTabBtn');
                                                                if (btn) btn.click();
                                                            }
                                                        }}
                                                    />
                                                    <button type="button" id="addTabBtn" className="btn btn-sm fw-bold d-flex align-items-center"
                                                        style={{ backgroundColor: '#1E2335', color: '#fff', borderRadius: '8px', padding: '0 12px', fontSize: '0.8rem', height: '28px' }}
                                                        onClick={() => {
                                                            const el = document.getElementById('newTabInput');
                                                            if (el && el.value.trim() !== '') {
                                                                const val = el.value.trim();
                                                                setForm(f => {
                                                                    if (f.tabs.includes(val)) return f;
                                                                    return { ...f, tabs: [...f.tabs, val] };
                                                                });
                                                                el.value = '';
                                                            }
                                                        }}>
                                                        <Plus size={13} className="me-1" />Add Tab
                                                    </button>
                                                </div>
                                            </div>
                                            {form.tabs.map(tab => (
                                                <div key={tab} className="mb-4" style={{ border: `1px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', padding: '15px', backgroundColor: 'transparent' }}>
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <h6 style={{ fontWeight: '700', color: colors?.textMain || '#fff', margin: 0 }}>Tab: {tab}</h6>
                                                        <div>
                                                            <button type="button" className="btn btn-sm fw-bold me-2"
                                                                style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '0.75rem' }}
                                                                onClick={() => addChoice(tab)}>
                                                                <Plus size={13} className="me-1" />Add Option
                                                            </button>
                                                            <button type="button" className="btn btn-sm fw-bold"
                                                                style={{ backgroundColor: '#ef4444', color: '#fff', borderRadius: '6px', padding: '3px 10px', fontSize: '0.75rem' }}
                                                                onClick={() => handleRemoveTab(tab)}>
                                                                Remove Tab
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        {form.choices.map((c, i) => ({ ...c, originalIdx: i })).filter(c => c.tab === tab).map((choice) => renderChoiceRow(choice, choice.originalIdx))}
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            )}
                        </form>

                        <ConditionalLogicSection
                            conditions={form.conditions}
                            options={options}
                            formKey={form.key}
                            colors={colors}
                            inputStyle={inputStyle}
                            onSetCond={setCond}
                            onSetCondRule={setCondRule}
                            onAddCondRule={addCondRule}
                            onRemoveCondRule={removeCondRule}
                            onAddCondGroup={addCondGroup}
                            onRemoveCondGroup={removeCondGroup}
                        />

                        {/* Modal Footer */}
                        <div className="d-flex gap-2 px-4 py-3"
                            style={{ borderTop: `1px solid ${colors?.borderColor || '#E9E4DB'}` }}>
                            <button type="button" className="btn flex-grow-1 fw-bold"
                                onClick={closeModal}
                                style={{ border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', color: colors?.textMuted }}>
                                Cancel
                            </button>
                            <button className="btn flex-grow-1 fw-bold d-flex align-items-center justify-content-center gap-2"
                                onClick={handleSave}
                                disabled={saving}
                                style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '12px' }}>
                                {saving
                                    ? <><span className="spinner-border spinner-border-sm"></span> Saving...</>
                                    : <><Check size={16} /> {editTarget ? 'Update' : 'Create'}</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── CATEGORY CREATE MODAL ───────────────────────────────────── */}
            {catModalOpen && (
                <div className="position-fixed w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ top: 0, left: 0, zIndex: 8100, backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="shadow-lg p-4"
                        style={{ ...cardStyle, borderRadius: '20px', width: '90%', maxWidth: '420px' }}>
                        <div className="d-flex align-items-center justify-content-between mb-4">
                            <h6 className="fw-bold m-0" style={{ color: colors?.textMain }}>
                                <FolderPlus size={16} className="me-2" style={{ color: '#C5A075' }} />New Category
                            </h6>
                            <button className="btn btn-sm" onClick={() => setCatModalOpen(false)}
                                style={{ border: 'none', backgroundColor: 'transparent', color: colors?.textMuted }}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="mb-3">
                            <label style={labelStyle}>Category Name <span style={{ color: '#ef4444' }}>*</span></label>
                            <input style={inputStyle}
                                value={catForm.label}
                                onChange={e => {
                                    const val = e.target.value;
                                    setCatForm(f => ({ ...f, label: val, name: slugify(val) }));
                                }}
                                placeholder="e.g. Strap Style" autoFocus />
                            {catForm.name && (
                                <small style={{ color: '#8B7D6B', fontSize: '0.72rem', marginTop: '4px', display: 'block' }}>
                                    Slug: <code>{catForm.name}</code>
                                </small>
                            )}
                        </div>
                        <div className="d-flex gap-2 mt-4">
                            <button type="button" className="btn flex-grow-1 fw-bold"
                                onClick={() => setCatModalOpen(false)}
                                style={{ border: `1.5px solid ${colors?.borderColor || '#E9E4DB'}`, borderRadius: '12px', color: colors?.textMuted }}>
                                Cancel
                            </button>
                            <button type="button" className="btn flex-grow-1 fw-bold"
                                onClick={handleSaveCategory}
                                disabled={catSaving}
                                style={{ backgroundColor: '#C5A075', color: '#fff', borderRadius: '12px' }}>
                                {catSaving ? <><span className="spinner-border spinner-border-sm"></span> Saving...</> : <><Check size={15} className="me-1" />Create</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Options;
